/**
 * Keyboard / mouse input.
 *
 * Two shapes of input, kept apart on purpose:
 * - **Continuous** (`frame`, `consumeMouseDelta`, `consumeWheelDelta`) — polled
 *   by the game loop, so a fast mouse never produces more camera motion than the
 *   frame it happened in.
 * - **Discrete** (`onKey`) — Escape, `~`, R. Edge-triggered, registered by
 *   `KeyboardEvent.code` so they work on any keyboard layout.
 *
 * Mouse deltas only accumulate under pointer lock: moving the mouse over the
 * start screen or the pause menu must not spin the camera behind it.
 */

import type { FrameInput } from '../physics/pogo';

/** Accumulated pointer motion since the last `consumeMouseDelta`. */
export interface MouseDelta {
  dx: number;
  dy: number;
}

export class Input {
  /** Pointer-lock state. Also gates mouse-delta accumulation. */
  isLocked = false;

  private readonly held = new Set<string>();
  private readonly keyHandlers = new Map<string, Array<() => void>>();
  private readonly lockHandlers: Array<(locked: boolean) => void> = [];

  private mouseDX = 0;
  private mouseDY = 0;
  private wheel = 0;
  private mouseDown = false;

  private readonly frameInput: FrameInput = { leanX: 0, leanZ: 0, charging: false };
  private readonly delta: MouseDelta = { dx: 0, dy: 0 };
  private disposed = false;

  constructor() {
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onBlur);
    window.addEventListener('mousemove', this.onMouseMove);
    window.addEventListener('mousedown', this.onMouseDown);
    window.addEventListener('mouseup', this.onMouseUp);
    window.addEventListener('wheel', this.onWheel, { passive: false });
    document.addEventListener('pointerlockchange', this.onLockChangeEvent);
  }

  // --- continuous ---------------------------------------------------------

  /**
   * Player intent for this frame.
   *
   * @returns a reused object — read it, do not retain it.
   */
  frame(): FrameInput {
    const f = this.frameInput;
    let x = 0;
    let z = 0;
    if (this.held.has('KeyD') || this.held.has('ArrowRight')) x += 1;
    if (this.held.has('KeyA') || this.held.has('ArrowLeft')) x -= 1;
    if (this.held.has('KeyW') || this.held.has('ArrowUp')) z += 1;
    if (this.held.has('KeyS') || this.held.has('ArrowDown')) z -= 1;

    // Diagonals must not out-lean the cardinals.
    const m = Math.hypot(x, z);
    if (m > 1) {
      x /= m;
      z /= m;
    }
    // Shift is the precision modifier: half lean, for threading a narrow ledge.
    if (this.held.has('ShiftLeft') || this.held.has('ShiftRight')) {
      x *= 0.5;
      z *= 0.5;
    }

    f.leanX = x;
    f.leanZ = z;
    f.charging = this.held.has('Space') || this.mouseDown;
    return f;
  }

  /** Pointer motion since the last call, in raw pixels. Zeroes the accumulator. */
  consumeMouseDelta(): MouseDelta {
    this.delta.dx = this.mouseDX;
    this.delta.dy = this.mouseDY;
    this.mouseDX = 0;
    this.mouseDY = 0;
    return this.delta;
  }

  /** Wheel motion since the last call, in raw deltaY. Zeroes the accumulator. */
  consumeWheelDelta(): number {
    const w = this.wheel;
    this.wheel = 0;
    return w;
  }

  // --- discrete -----------------------------------------------------------

  /**
   * Fire `cb` on each press (not repeat) of `code`, e.g. `'Escape'`,
   * `'Backquote'`, `'KeyR'`.
   *
   * @returns an unsubscribe function.
   */
  onKey(code: string, cb: () => void): () => void {
    let list = this.keyHandlers.get(code);
    if (list === undefined) {
      list = [];
      this.keyHandlers.set(code, list);
    }
    list.push(cb);
    return () => {
      const l = this.keyHandlers.get(code);
      if (l === undefined) return;
      const i = l.indexOf(cb);
      if (i >= 0) l.splice(i, 1);
    };
  }

  // --- pointer lock -------------------------------------------------------

  /** Ask for pointer lock on `el`. Must be called from a user gesture. */
  requestLock(el: HTMLElement): void {
    const req = el.requestPointerLock() as unknown;
    // Chrome returns a promise; a rejection here is normal (user pressed Esc).
    if (req instanceof Promise) req.catch(() => undefined);
  }

  /** Release pointer lock, if we hold it. */
  exitLock(): void {
    if (document.pointerLockElement !== null) document.exitPointerLock();
  }

  /** @returns an unsubscribe function. */
  onLockChange(cb: (locked: boolean) => void): () => void {
    this.lockHandlers.push(cb);
    return () => {
      const i = this.lockHandlers.indexOf(cb);
      if (i >= 0) this.lockHandlers.splice(i, 1);
    };
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onBlur);
    window.removeEventListener('mousemove', this.onMouseMove);
    window.removeEventListener('mousedown', this.onMouseDown);
    window.removeEventListener('mouseup', this.onMouseUp);
    window.removeEventListener('wheel', this.onWheel);
    document.removeEventListener('pointerlockchange', this.onLockChangeEvent);
    this.held.clear();
    this.keyHandlers.clear();
    this.lockHandlers.length = 0;
  }

  // --- listeners ----------------------------------------------------------

  private readonly onKeyDown = (e: KeyboardEvent): void => {
    // Space scrolls the page and Tab leaves the canvas; neither is wanted mid-run.
    if (e.code === 'Space' || e.code === 'Tab') e.preventDefault();
    // Auto-repeat still re-adds the key: refocusing the window with W held would
    // otherwise leave it stuck released until the player taps it again.
    const fresh = !e.repeat && !this.held.has(e.code);
    this.held.add(e.code);
    if (!fresh) return;
    const list = this.keyHandlers.get(e.code);
    if (list !== undefined) for (const cb of list.slice()) cb();
  };

  private readonly onKeyUp = (e: KeyboardEvent): void => {
    this.held.delete(e.code);
  };

  /** Losing focus with W held must not leave the player leaning forever. */
  private readonly onBlur = (): void => {
    this.held.clear();
    this.mouseDown = false;
    this.mouseDX = 0;
    this.mouseDY = 0;
  };

  private readonly onMouseMove = (e: MouseEvent): void => {
    if (!this.isLocked) return;
    this.mouseDX += e.movementX;
    this.mouseDY += e.movementY;
  };

  private readonly onMouseDown = (e: MouseEvent): void => {
    if (e.button === 0) this.mouseDown = true;
  };

  private readonly onMouseUp = (e: MouseEvent): void => {
    if (e.button === 0) this.mouseDown = false;
  };

  private readonly onWheel = (e: WheelEvent): void => {
    if (!this.isLocked) return;
    e.preventDefault();
    this.wheel += e.deltaY;
  };

  private readonly onLockChangeEvent = (): void => {
    this.isLocked = document.pointerLockElement !== null;
    if (!this.isLocked) {
      // Dropping lock mid-hold would otherwise leave the charge stuck on.
      this.held.clear();
      this.mouseDown = false;
    }
    for (const cb of this.lockHandlers.slice()) cb(this.isLocked);
  };
}
